import { create } from "zustand";
import { persist } from "zustand/middleware";
import * as types from "_graphql-types/graphql";

export type EntityTypeFilter = "ALL" | "NOTE" | "DOCUMENT";

interface EntityFilter {
  investmentId?: number;
  firmId?: number;
}

// Filter value types
export interface FilterValue {
  id: number | string;
  label: string;
}

// Performance filter: a public return or a private metric, ranged in the
// unit the backend stores it in (public returns are fractions: 0.05 = 5%)
export type PerformanceFilterValue =
  | {
      type: "PERFORMANCE";
      statType: types.PerformanceStatType;
      min?: number;
      max?: number;
    }
  | {
      type: "PERFORMANCE_PRIVATE";
      statType: types.PerformancePrivateStatType;
      min?: number;
      max?: number;
    };

// Active filters structure
export interface ActiveFilters {
  // Allow dynamic key access for filter components
  [key: string]:
    | FilterValue[]
    | string[]
    | number[]
    | string
    | { min?: number; max?: number }
    | PerformanceFilterValue
    | undefined;

  // Knowledge Base filters
  entityTypes?: FilterValue[]; // Notes, Documents
  dateStart?: string; // Knowledge base date (noteDate/document.date)
  dateEnd?: string;
  dateRelative?: string; // Relative date shorthand (e.g., "1m", "mtd", "ytd", "3y")
  modifyDateStart?: string;
  modifyDateEnd?: string;
  modifyDateRelative?: string; // Relative date shorthand for modify date
  knowledgeBaseTagIds?: FilterValue[]; // Knowledge base tags

  // Note filters
  noteCreateUsers?: FilterValue[];
  noteMetaIds?: FilterValue[];

  // Document filters
  documentGroups?: FilterValue[];
  documentCategories?: FilterValue[];
  documentTypes?: string[]; // file extensions
  documentFormats?: FilterValue[]; // file format filter (.pdf, .doc, etc.)

  // Investment filters
  investmentIds?: FilterValue[];
  investmentListIds?: FilterValue[]; // Investment Sets/Lists
  assetClasses?: FilterValue[];
  investmentTypes?: FilterValue[];
  geographicFocus?: FilterValue[];
  geographies?: FilterValue[]; // Primary geography filter
  size?: { min?: number; max?: number }; // Fund size range
  performance?: PerformanceFilterValue;
  sizes?: FilterValue[];
  themes?: FilterValue[];
  ownerships?: FilterValue[];
  investmentTags?: FilterValue[]; // General investment tags by tag class
  headquarters?: FilterValue[];
  vintageYears?: number[];

  // Firm filters
  firmIds?: FilterValue[];

  // Company filters
  companyIds?: FilterValue[];
  companyHeadquarters?: FilterValue[];
  yearsFound?: number[];
  companyIndustries?: FilterValue[];
  industries?: FilterValue[]; // Industry tags
}

interface KnowledgeBaseStore {
  // Search state
  searchText: string;
  setSearchText: (text: string) => void;

  // Sort state
  sortField: string;
  sortDirection: "ASC" | "DESC";
  setSortField: (field: string) => void;
  setSortDirection: (direction: "ASC" | "DESC") => void;

  // Filter state
  entityType: EntityTypeFilter;
  setEntityType: (type: EntityTypeFilter) => void;

  // Active filters
  activeFilters: ActiveFilters;
  setActiveFilters: (filters: ActiveFilters) => void;
  clearActiveFilters: () => void;

  // Unified filter dropdown state
  openFilterId: string | null;
  setOpenFilterId: (id: string | null) => void;

  // Entity filter (investment/firm - passed as prop, stored for query)
  entityFilter?: EntityFilter;
  setEntityFilter: (filter?: EntityFilter) => void;

  // Results state
  items: types.KnowledgeBaseItem[];
  setItems: (items: types.KnowledgeBaseItem[]) => void;
  appendItems: (items: types.KnowledgeBaseItem[]) => void;

  // Pagination
  hasMore: boolean;
  setHasMore: (hasMore: boolean) => void;
  offset: number;
  setOffset: (offset: number) => void;
  total: number;
  setTotal: (total: number) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Currently loaded saved KB (set when viewing /knowledgeBase/:id). When non-null,
  // Save flows update this KB in place rather than creating a new one.
  loadedKnowledgeBaseId: number | null;
  loadedKnowledgeBaseName: string | null;
  loadedKnowledgeBaseGroupId: number | null;
  // Raw GraphQL-shaped filters JSON from the saved KB, used to detect unsaved
  // changes by diffing against the current (re-derived) filter payload.
  loadedKnowledgeBaseFilters: unknown | null;
  setLoadedKnowledgeBase: (
    knowledgeBase: {
      id: number;
      name: string;
      groupId: number | null;
      filters: unknown;
    } | null
  ) => void;

  // Recent searches (persisted to localStorage, only if no entityFilter)
  recentSearches: Array<{
    text: string;
    filters: ActiveFilters;
    timestamp: number;
  }>;
  addRecentSearch: (search: string, filters: ActiveFilters) => void;
  clearRecentSearches: () => void;

  // Actions
  reset: () => void;
}

const INITIAL_STATE = {
  searchText: "",
  sortField: "modifyDate",
  sortDirection: "DESC" as "ASC" | "DESC",
  entityType: "ALL" as EntityTypeFilter,
  entityFilter: undefined,
  // No default Item Type: the backend infers it from type-specific filters
  // (#5254), which an explicit [NOTE, DOCUMENT] default would override.
  activeFilters: {},
  openFilterId: null,
  items: [],
  hasMore: true,
  offset: 0,
  total: 0,
  isLoading: false,
  loadedKnowledgeBaseId: null,
  loadedKnowledgeBaseName: null,
  loadedKnowledgeBaseGroupId: null,
  loadedKnowledgeBaseFilters: null,
  recentSearches: [],
};

// Create the store with proper typing
const store = create<KnowledgeBaseStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setSearchText: (text: string) => set({ searchText: text }),

      setSortField: (field: string) =>
        set({ sortField: field, offset: 0, items: [] }),

      setSortDirection: (direction: "ASC" | "DESC") =>
        set({ sortDirection: direction, offset: 0, items: [] }),

      setEntityType: (type: EntityTypeFilter) =>
        set({ entityType: type, offset: 0, items: [] }),

      setActiveFilters: (filters: ActiveFilters) =>
        set({ activeFilters: filters, offset: 0, items: [] }),

      clearActiveFilters: () =>
        set({ activeFilters: {}, offset: 0, items: [] }),

      setOpenFilterId: (id: string | null) => set({ openFilterId: id }),

      setEntityFilter: (filter?: EntityFilter) =>
        set({ entityFilter: filter, offset: 0, items: [] }),

      setItems: (items: types.KnowledgeBaseItem[]) => set({ items }),

      appendItems: (newItems: types.KnowledgeBaseItem[]) =>
        set(state => ({ items: [...state.items, ...newItems] })),

      setHasMore: (hasMore: boolean) => set({ hasMore }),

      setOffset: (offset: number) => set({ offset }),

      setTotal: (total: number) => set({ total }),

      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      setLoadedKnowledgeBase: kb =>
        set({
          loadedKnowledgeBaseId: kb?.id ?? null,
          loadedKnowledgeBaseName: kb?.name ?? null,
          loadedKnowledgeBaseGroupId: kb?.groupId ?? null,
          loadedKnowledgeBaseFilters: kb?.filters ?? null,
        }),

      addRecentSearch: (search: string, filters: ActiveFilters) => {
        const { entityFilter, recentSearches } = get();

        // Check if there are any non-empty filters
        const hasActiveFilters = Object.values(filters).some(value => {
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === "object" && value !== null) {
            return Object.values(value).some(
              v => v !== undefined && v !== null
            );
          }
          return value !== undefined && value !== null && value !== "";
        });

        // Only add to recent searches if no entity filter is active AND (has search text OR has filters)
        if (!entityFilter && (search.trim() || hasActiveFilters)) {
          const trimmedSearch = search.trim();

          // Create a stable key from search text + filters for comparison
          const createSearchKey = (text: string, f: ActiveFilters) => {
            return JSON.stringify({ text, filters: f });
          };

          const newSearchKey = createSearchKey(trimmedSearch, filters);

          const newSearch = {
            text: trimmedSearch,
            filters,
            timestamp: Date.now(),
          };

          // Remove any existing search with the same text+filters combination
          const updated = [
            newSearch,
            ...recentSearches.filter(
              s => createSearchKey(s.text, s.filters) !== newSearchKey
            ),
          ].slice(0, 20); // Keep only last 20
          set({ recentSearches: updated });
        }
      },

      clearRecentSearches: () => set({ recentSearches: [] }),
      reset: () => set(INITIAL_STATE),
    }),
    {
      name: "knowledge-base-store",
      partialize: state => ({
        recentSearches: state.recentSearches,
      }),
    }
  )
);

// Export the store hook
export const useKnowledgeBaseStore = store;

// Export the store type for use in selectors
export type { KnowledgeBaseStore };
